// src/components/human-capital/team/TeamList.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, Filter, PlusCircle, ChevronRight, AlertCircle, User, 
  Mail, Phone, Briefcase, X, Users, Building
} from 'lucide-react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuth } from '../../../context/AuthContext';
import { useContext } from 'react';
import { AppContext } from '../../../context/AppContext';

const TeamList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterBusiness, setFilterBusiness] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [departments, setDepartments] = useState(['all']);
  const [businesses, setBusinesses] = useState([]);
  const [error, setError] = useState(null);

  const { currentUser } = useAuth();
  const { activeBusiness } = useContext(AppContext);

  // Fetch team members and related data
  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!currentUser) return;
      
      setIsLoading(true);
      setError(null);

      try {
        // First, fetch all businesses the user has access to
        const businessesRef = collection(db, 'businesses');
        const businessQuery = query(
          businessesRef,
          where('createdBy', '==', currentUser.uid)
        );
        
        const businessSnapshot = await getDocs(businessQuery);
        
        const businessList = [];
        if (!businessSnapshot.empty) {
          businessSnapshot.forEach(doc => {
            businessList.push({
              id: doc.id,
              name: doc.data().name
            });
          });
          
          setBusinesses(businessList);
        }

        // Set initial filter to active business if available
        if (activeBusiness && filterBusiness === 'all') {
          setFilterBusiness(activeBusiness.id);
        }
        
        // Now fetch team members
        let teamQuery;
        if (filterBusiness !== 'all') {
          // If a business is selected, filter by that business
          teamQuery = query(
            collection(db, 'team_members'),
            where('businessId', '==', filterBusiness),
            orderBy('lastName')
          );
        } else {
          // Fetch all team members the user created
          teamQuery = query(
            collection(db, 'team_members'),
            orderBy('lastName')
          );
        }
        
        const teamSnapshot = await getDocs(teamQuery);
        
        const membersList = [];
        const departmentSet = new Set(['all']);
        
        if (!teamSnapshot.empty) {
          teamSnapshot.forEach(doc => {
            const memberData = doc.data();
            
            // Add to departments set for filtering
            if (memberData.department) {
              departmentSet.add(memberData.department);
            }
            
            membersList.push({
              id: doc.id,
              ...memberData
            });
          });
        }
        
        setTeamMembers(membersList);
        setDepartments(Array.from(departmentSet));
        
      } catch (err) {
        console.error('Error fetching team members:', err);
        setError('Failed to load team members. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchTeamMembers();
  }, [currentUser, activeBusiness, filterBusiness]);

  // Get filtered team members
  const getFilteredTeamMembers = () => {
    let filtered = teamMembers;
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(member => 
        (member.firstName && member.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (member.lastName && member.lastName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (member.position && member.position.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (member.email && member.email.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Filter by department
    if (filterDepartment !== 'all') {
      filtered = filtered.filter(member => member.department === filterDepartment);
    }
    
    return filtered;
  };

  // Get member status class
  const getStatusClass = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'on-leave':
        return 'bg-yellow-100 text-yellow-800';
      case 'contractor':
        return 'bg-blue-100 text-blue-800';
      case 'terminated':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format status text
  const formatStatus = (status) => {
    switch (status) {
      case 'on-leave':
        return 'On Leave';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  // Find business name by ID
  const getBusinessName = (businessId) => {
    const business = businesses.find(b => b.id === businessId);
    return business ? business.name : 'Unknown Business';
  };

  return (
    <div>
      {/* Search and Actions Bar */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            className="pl-10 pr-4 py-2.5 w-full rounded-lg text-sm border border-gray-200 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
            placeholder="Search by name, position, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="flex gap-3">
          {/* Business Filter */}
          {businesses.length > 0 && (
            <select 
              className="px-4 py-2.5 rounded-lg border border-gray-200 text-gray-600 bg-white focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
              value={filterBusiness}
              onChange={(e) => setFilterBusiness(e.target.value)}
            >
              <option value="all">All Businesses</option>
              {businesses.map(business => (
                <option key={business.id} value={business.id}>{business.name}</option>
              ))}
            </select>
          )}
          
          {/* Department Filter */}
          <select 
            className="px-4 py-2.5 rounded-lg border border-gray-200 text-gray-600 bg-white focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
            value={filterDepartment}
            onChange={(e) => setFilterDepartment(e.target.value)}
          >
            <option value="all">All Departments</option>
            {departments.filter(d => d !== 'all').map(department => (
              <option key={department} value={department}>{department}</option>
            ))}
          </select>
          
          <Link
            to="/human-capital/team/new"
            className="px-4 py-2.5 rounded-lg bg-[#190192] text-white flex items-center hover:bg-[#2C0DB5]"
          >
            <PlusCircle size={18} className="mr-2" />
            Add Team Member
          </Link>
        </div>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
        </div>
      ) : (
        <>
          {/* Error State */}
          {error && (
            <div className="bg-red-50 rounded-lg p-4 mb-6 text-red-700 flex items-center">
              <AlertCircle size={20} className="mr-2 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}
          
          {/* Empty State */}
          {teamMembers.length === 0 ? (
            <div className="bg-gray-50 rounded-lg py-10 px-6 text-center">
              <Users size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No team members found</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                You haven't added any team members yet. Click the 'Add Team Member' button to get started.
              </p>
              <Link
                to="/human-capital/team/new"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#190192] hover:bg-[#2C0DB5]"
              >
                <PlusCircle size={16} className="mr-2" />
                Add Your First Team Member
              </Link>
            </div>
          ) : getFilteredTeamMembers().length === 0 ? (
            <div className="bg-gray-50 rounded-lg py-10 px-6 text-center">
              <Users size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No matching team members</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                We couldn't find any team members matching your search criteria. Try adjusting your search or filters.
              </p>
              <button
                onClick={() => { setSearchTerm(''); setFilterDepartment('all'); }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            /* Team Members List/Table */
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Position
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact Info
                      </th>
                      {filterBusiness === 'all' && (
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Business
                        </th>
                      )}
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Department
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getFilteredTeamMembers().map((member) => (
                      <tr key={member.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 rounded-full">
                              {member.avatarUrl ? (
                                <img 
                                  src={member.avatarUrl} 
                                  alt={`${member.firstName} ${member.lastName}`}
                                  className="h-10 w-10 rounded-full object-cover"
                                />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-800">
                                  <User size={20} />
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {`${member.firstName} ${member.lastName}`}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Briefcase size={16} className="text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">{member.position}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <Mail size={14} className="mr-1 text-gray-400" /> {member.email}
                          </div>
                          {member.phone && (
                            <div className="text-sm text-gray-500 flex items-center">
                              <Phone size={14} className="mr-1 text-gray-400" /> {member.phone}
                            </div>
                          )}
                        </td>
                        {filterBusiness === 'all' && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-900">
                              <Building size={14} className="mr-1 text-gray-400" />
                              {getBusinessName(member.businessId)}
                            </div>
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            {member.department}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClass(member.status)}`}>
                            {formatStatus(member.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Link
                            to={`/human-capital/team/${member.id}`}
                            className="text-[#190192] hover:text-[#2C0DB5] inline-flex items-center"
                          >
                            View <ChevronRight size={16} className="ml-1" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TeamList;